// src/pages/TeamBattlePage.js — Team Battle 2v2/3v3 asynchrone
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection, doc, addDoc, updateDoc, getDoc, onSnapshot,
  query, where, orderBy, serverTimestamp, arrayUnion, Timestamp
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { computeLevel } from "../data/demons";
import "./TeamBattlePage.css";


function buildTeamNarrationPrompt(team1, team2, duels) {
  const t1score = duels.filter(d => d.winner === "team1").length;
  const t2score = duels.filter(d => d.winner === "team2").length;
  const winner  = t1score > t2score ? team1 : team2;

  return `Tu es le Narrateur des Guerres Démoniaques de Takkyu Oni. Narre ce Team Battle en 3-4 phrases épiques.

TEAM BATTLE :
🔴 ${team1.name} (${team1.members.map(m=>m.oniName).join(", ")})
🔵 ${team2.name} (${team2.members.map(m=>m.oniName).join(", ")})

DUELS :
${duels.map((d,i)=>`Duel ${i+1}: ${d.p1Name} (${d.p1Oni}) vs ${d.p2Name} (${d.p2Oni}) → ${d.winner==="team1"?d.p1Name:d.p2Name} gagne`).join("\n")}

Score final : ${t1score} - ${t2score} pour ${winner.name}

Génère en JSON :
{
  "narration": "3-4 phrases épiques sur le combat collectif. Style anime de sport. Mentionne les démons par nom.",
  "victory_cry": "Cri de victoire court de l'équipe gagnante (6 mots max)",
  "defeat_line": "Ligne de défaite de l'équipe perdante (6 mots max)"
}
JSON uniquement.`;
}

// ── Team Card ─────────────────────────────────────────────────────────────
function TeamCard({ team, onJoin, canJoin, isInTeam }) {
  const isFull = team.members.length >= team.size;
  return (
    <div className="tb-team-card">
      <div className="tb-team-header">
        <div className="tb-team-emojis">
          {team.members.map((m, i) => (
            <span key={i} title={m.displayName}>{m.oniEmoji || "👹"}</span>
          ))}
          {Array(team.size - team.members.length).fill(null).map((_, i) => (
            <span key={`empty-${i}`} className="tb-team-slot-empty">？</span>
          ))}
        </div>
        <div>
          <p className="tb-team-name">{team.name}</p>
          <p className="tb-team-meta">{team.members.length}/{team.size} membres · {team.size}v{team.size}</p>
        </div>
        <span className={`tb-team-status-badge ${team.status}`}>{team.status.toUpperCase()}</span>
      </div>

      <div className="tb-team-members">
        {team.members.map((m, i) => (
          <div key={i} className="tb-member-row">
            <span>{m.oniEmoji}</span>
            <div>
              <p className="tb-member-name">{m.displayName}</p>
              <p className="tb-member-oni">{m.oniName}</p>
            </div>
            {m.duelScore !== undefined && (
              <span className="tb-member-score">{m.duelScore > 0 ? `+${m.duelScore}` : m.duelScore}</span>
            )}
          </div>
        ))}
      </div>

      {canJoin && !isFull && team.status === "recruiting" && (
        <button className="btn btn-primary btn-sm tb-join-btn" onClick={() => onJoin(team.id)}>
          REJOINDRE LA TEAM →
        </button>
      )}
      {isFull && team.status === "recruiting" && (
        <p className="tb-team-full">TEAM COMPLÈTE</p>
      )}
    </div>
  );
}

// ── Duel result row ───────────────────────────────────────────────────────
function DuelRow({ duel, myUid }) {
  const isP1 = duel.p1Uid === myUid;
  const isP2 = duel.p2Uid === myUid;
  const won  = (isP1 && duel.winner === "team1") || (isP2 && duel.winner === "team2");
  return (
    <div className={`tb-duel-row${won ? " won" : duel.winner ? " lost" : " pending"}`}>
      <div className="tb-duel-players">
        <span>{duel.p1Emoji} {duel.p1Name}</span>
        <span className="tb-vs">VS</span>
        <span>{duel.p2Emoji} {duel.p2Name}</span>
      </div>
      <span className={`tb-duel-status ${duel.winner ? "done" : "pending"}`}>
        {duel.winner
          ? (duel.winner === "team1" ? `${duel.p1Name} gagne` : `${duel.p2Name} gagne`)
          : "EN ATTENTE"}
      </span>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────
export default function TeamBattlePage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab]           = useState("browse"); // browse | create | mybattle
  const [battles, setBattles]   = useState([]);
  const [myBattle, setMyBattle] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [creating, setCreating] = useState(false);

  // Create form state
  const [teamName, setTeamName]   = useState("");
  const [battleSize, setBattleSize] = useState(2);
  const [battleName, setBattleName] = useState("");

  const oni   = profile?.demon?.oni;
  const myTeamId = profile?.currentTeamId;

  // Load active battles
  useEffect(() => {
    const q = query(collection(db, "team_battles"), orderBy("createdAt", "desc"), where("status", "in", ["recruiting","in_progress","completed"]));
    return onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setBattles(all);
      const mine = all.find(b =>
        b.team1.members.some(m => m.uid === user?.uid) ||
        b.team2.members.some(m => m.uid === user?.uid)
      );
      setMyBattle(mine || null);
      if (mine) setTab("mybattle");
      setLoading(false);
    });
  }, [user]);

  const createBattle = async () => {
    if (!teamName.trim() || !battleName.trim() || !user || !oni) return;
    setCreating(true);
    const myMember = {
      uid: user.uid, displayName: profile?.displayName || "Oni",
      oniName: oni.name, oniEmoji: oni.emoji, oniJp: oni.jp,
      demon: profile?.demon
    };
    try {
      await addDoc(collection(db, "team_battles"), {
        name: battleName.trim(),
        size: battleSize,
        status: "recruiting",
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        team1: { name: teamName.trim(), members: [myMember] },
        team2: { name: "Adversaires", members: [] },
        duels: [],
        narration: null,
      });
      await updateDoc(doc(db, "users", user.uid), { currentTeamId: "new" });
    } catch(e) { console.error(e); }
    setCreating(false);
    setTab("browse");
  };

  const joinTeam = async (battleId, teamSlot) => {
    if (!user || !oni) return;
    const battleRef = doc(db, "team_battles", battleId);
    const snap = await getDoc(battleRef);
    if (!snap.exists()) return;
    const battle = snap.data();
    const team = battle[teamSlot];
    if (team.members.length >= battle.size) return;

    const myMember = {
      uid: user.uid, displayName: profile?.displayName || "Oni",
      oniName: oni.name, oniEmoji: oni.emoji, oniJp: oni.jp,
      demon: profile?.demon
    };

    const updatedTeam = { ...team, members: [...team.members, myMember] };
    const update = { [teamSlot]: updatedTeam };

    // Check if both teams are full — start battle
    const otherSlot = teamSlot === "team1" ? "team2" : "team1";
    const otherTeam = battle[otherSlot];
    if (updatedTeam.members.length >= battle.size && otherTeam.members.length >= battle.size) {
      // Generate duels
      const duels = updatedTeam.members.map((m, i) => {
        const opponent = otherTeam.members[i % otherTeam.members.length];
        return {
          id: `duel_${i}`,
          p1Uid: teamSlot === "team1" ? m.uid : opponent.uid,
          p1Name: teamSlot === "team1" ? m.displayName : opponent.displayName,
          p1Emoji: teamSlot === "team1" ? m.oniEmoji : opponent.oniEmoji,
          p2Uid: teamSlot === "team1" ? opponent.uid : m.uid,
          p2Name: teamSlot === "team1" ? opponent.displayName : m.displayName,
          p2Emoji: teamSlot === "team1" ? opponent.oniEmoji : m.oniEmoji,
          winner: null, score1: 0, score2: 0,
        };
      });
      update.status = "in_progress";
      update.duels = duels;
    }

    await updateDoc(battleRef, update);
  };

  const playMyDuel = async (battle) => {
    const myDuel = battle.duels.find(d => (d.p1Uid === user.uid || d.p2Uid === user.uid) && !d.winner);
    if (!myDuel) return;

    const isP1    = myDuel.p1Uid === user.uid;
    const myScore = Math.round(30 + Math.random() * 50);
    const opScore = Math.round(30 + Math.random() * 50);
    const iWin    = myScore > opScore;

    const updatedDuels = battle.duels.map(d => {
      if (d.id !== myDuel.id) return d;
      return {
        ...d,
        score1: isP1 ? myScore : opScore,
        score2: isP1 ? opScore : myScore,
        winner: iWin ? (isP1 ? "team1" : "team2") : (isP1 ? "team2" : "team1"),
        playedAt: new Date().toISOString(),
      };
    });

    const battleRef = doc(db, "team_battles", battle.id);
    const allDone   = updatedDuels.every(d => d.winner);
    const update    = { duels: updatedDuels };

    if (allDone) {
      // Generate team narration + close battle
      try {
        const CLAUDE_URL = `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/claude`;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        const res = await fetch(CLAUDE_URL, {
          method: "POST", signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: "claude-sonnet-4-5",
            max_tokens: 500,
            messages: [{ role:"user", content: buildTeamNarrationPrompt(battle.team1, battle.team2, updatedDuels) }]
          })
        });
        clearTimeout(timeout);
        const data = await res.json();
        const text = data.content?.find(b=>b.type==="text")?.text||"";
        const narration = JSON.parse(text.replace(/```json|```/g,"").trim());
        update.narration = narration;
      } catch(e) { update.narration = { narration:"Le combat collectif s'est terminé dans le chaos.", victory_cry:"Le chaos règne !", defeat_line:"Qu'il en soit ainsi." }; }
      update.status = "completed";

      // XP for winners
      const t1w = updatedDuels.filter(d=>d.winner==="team1").length;
      const t2w = updatedDuels.filter(d=>d.winner==="team2").length;
      const winnerTeam = t1w > t2w ? battle.team1 : battle.team2;
      const xpGain = 75;
      for (const m of winnerTeam.members) {
        await updateDoc(doc(db,"users",m.uid), {
          xp: (profile?.xp||0) + xpGain,
          weeklyDuelsWon: (profile?.weeklyDuelsWon||0) + 1
        }).catch(()=>{});
      }
    }
    await updateDoc(battleRef, update);
  };

  if (!user) return (
    <div className="tb-page section" style={{paddingTop:"8rem", textAlign:"center"}}>
      <span className="section-eyebrow">団体戦 · Team Battle</span>
      <h2 className="section-title">Guerres Démoniaques</h2>
      <div className="divider divider-center"/>
      <Link to="/auth" className="btn btn-primary" style={{marginTop:"1.5rem"}}>ENTRER →</Link>
    </div>
  );

  if (!oni) return (
    <div className="tb-page section" style={{paddingTop:"8rem", textAlign:"center"}}>
      <h2 className="section-title">Crée ton démon<br/>pour rejoindre une team</h2>
      <Link to="/awaken" className="btn btn-primary" style={{marginTop:"1.5rem"}}>RÉVEILLER →</Link>
    </div>
  );

  const openBattles = battles.filter(b => b.status === "recruiting");
  const activeBattles = battles.filter(b => b.status === "in_progress");
  const completedBattles = battles.filter(b => b.status === "completed");

  return (
    <div className="tb-page">
      <div className="section" style={{paddingTop:"7rem"}}>

        <div className="tb-header">
          <div>
            <span className="section-eyebrow">団体戦 · Guerres</span>
            <h1 className="section-title">Team Battle</h1>
            <div className="divider"/>
            <p className="section-sub">2v2 ou 3v3 asynchrone. Chaque membre duel. Les scores s'additionnent.</p>
          </div>
          <div className="tb-my-oni">
            <span>{oni.emoji}</span>
            <div>
              <p className="tb-my-oni-jp">{oni.jp}</p>
              <p className="tb-my-oni-name">{oni.name}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tb-tabs">
          <button className={`lb-tab${tab==="browse"?" active":""}`} onClick={()=>setTab("browse")}>
            🔍 BATTLES OUVERTES
          </button>
          {myBattle && (
            <button className={`lb-tab${tab==="mybattle"?" active":""}`} onClick={()=>setTab("mybattle")}>
              ⚔️ MA BATTLE
            </button>
          )}
          <button className={`lb-tab${tab==="create"?" active":""}`} onClick={()=>setTab("create")}>
            + CRÉER
          </button>
        </div>

        {/* Browse */}
        {tab === "browse" && (
          <div className="tb-browse">
            {loading ? (
              <div style={{textAlign:"center", padding:"3rem"}}><span className="spinner" style={{width:24,height:24}}/></div>
            ) : (
              <>
                {openBattles.length > 0 && (
                  <div className="tb-section">
                    <p className="tb-section-label">EN RECRUTEMENT · {openBattles.length}</p>
                    <div className="tb-battles-grid">
                      {openBattles.map(b => (
                        <div key={b.id} className="tb-battle-card">
                          <div className="tb-battle-header">
                            <p className="tb-battle-name">{b.name}</p>
                            <span className="badge badge-ember">{b.size}v{b.size}</span>
                          </div>
                          <div className="tb-battle-teams">
                            <TeamCard team={b.team1} onJoin={() => joinTeam(b.id,"team1")}
                              canJoin={!myBattle} isInTeam={b.team1.members.some(m=>m.uid===user.uid)}/>
                            <div className="tb-battle-vs">VS</div>
                            <TeamCard team={b.team2} onJoin={() => joinTeam(b.id,"team2")}
                              canJoin={!myBattle} isInTeam={b.team2.members.some(m=>m.uid===user.uid)}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeBattles.length > 0 && (
                  <div className="tb-section">
                    <p className="tb-section-label">EN COURS · {activeBattles.length}</p>
                    <div className="tb-battles-grid">
                      {activeBattles.map(b => (
                        <div key={b.id} className="tb-battle-card">
                          <div className="tb-battle-header">
                            <p className="tb-battle-name">{b.name}</p>
                            <span className="badge badge-blood">EN COURS</span>
                          </div>
                          <div className="tb-score-display">
                            <div className="tb-score-team">
                              <span>{b.team1.members[0]?.oniEmoji}</span>
                              <p>{b.team1.name}</p>
                              <p className="tb-score-num">{b.duels.filter(d=>d.winner==="team1").length}</p>
                            </div>
                            <span className="tb-score-sep">:</span>
                            <div className="tb-score-team">
                              <p className="tb-score-num">{b.duels.filter(d=>d.winner==="team2").length}</p>
                              <p>{b.team2.name}</p>
                              <span>{b.team2.members[0]?.oniEmoji}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {completedBattles.length > 0 && (
                  <div className="tb-section">
                    <p className="tb-section-label">TERMINÉES · {completedBattles.length}</p>
                    <div className="tb-battles-grid">
                      {completedBattles.slice(0,4).map(b => {
                        const t1 = b.duels.filter(d=>d.winner==="team1").length;
                        const t2 = b.duels.filter(d=>d.winner==="team2").length;
                        const winner = t1 > t2 ? b.team1.name : b.team2.name;
                        return (
                          <div key={b.id} className="tb-battle-card completed">
                            <div className="tb-battle-header">
                              <p className="tb-battle-name">{b.name}</p>
                              <span className="badge badge-ghost">TERMINÉ</span>
                            </div>
                            <p className="tb-completed-score">{t1} — {t2}</p>
                            <p className="tb-completed-winner">🏆 {winner}</p>
                            {b.narration?.narration && (
                              <p className="tb-completed-narration">{b.narration.narration}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {openBattles.length === 0 && activeBattles.length === 0 && (
                  <div className="lb-empty">
                    <span>⚔️</span>
                    <p>Aucune battle en cours. Crée la première !</p>
                    <button className="btn btn-primary btn-sm" style={{marginTop:"1rem"}} onClick={()=>setTab("create")}>
                      CRÉER UNE BATTLE →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* My battle */}
        {tab === "mybattle" && myBattle && (
          <div className="tb-mybattle">
            <div className="tb-mybattle-header card">
              <div className="tb-mybattle-title">
                <p className="tb-battle-name">{myBattle.name}</p>
                <span className={`badge${myBattle.status==="in_progress"?" badge-blood":myBattle.status==="completed"?" badge-ghost":" badge-ember"}`}>
                  {myBattle.status.toUpperCase()}
                </span>
              </div>

              {/* Score */}
              <div className="tb-score-display large">
                <div className="tb-score-team">
                  <span>{myBattle.team1.members[0]?.oniEmoji}</span>
                  <p>{myBattle.team1.name}</p>
                  <p className="tb-score-num">{myBattle.duels.filter(d=>d.winner==="team1").length}</p>
                </div>
                <span className="tb-score-sep">:</span>
                <div className="tb-score-team">
                  <p className="tb-score-num">{myBattle.duels.filter(d=>d.winner==="team2").length}</p>
                  <p>{myBattle.team2.name}</p>
                  <span>{myBattle.team2.members[0]?.oniEmoji}</span>
                </div>
              </div>
            </div>

            {/* My duel */}
            {myBattle.status === "in_progress" && (() => {
              const myDuel = myBattle.duels.find(d=>(d.p1Uid===user.uid||d.p2Uid===user.uid)&&!d.winner);
              return myDuel ? (
                <div className="tb-my-duel card">
                  <p className="tb-section-label">TON DUEL EN ATTENTE</p>
                  <div className="tb-my-duel-players">
                    <span>{myDuel.p1Emoji} {myDuel.p1Name}</span>
                    <span className="tb-vs">VS</span>
                    <span>{myDuel.p2Emoji} {myDuel.p2Name}</span>
                  </div>
                  <button className="btn btn-primary" style={{width:"100%", justifyContent:"center", marginTop:"1rem"}}
                    onClick={() => playMyDuel(myBattle)}>
                    ⚔️ JOUER CE DUEL
                  </button>
                </div>
              ) : (
                <div className="tb-waiting card">
                  <p>✓ Ton duel est terminé. En attente des autres membres...</p>
                </div>
              );
            })()}

            {/* All duels */}
            <div className="tb-duels-list card">
              <p className="tb-section-label">DUELS · {myBattle.duels.length}</p>
              {myBattle.duels.map((d,i) => (
                <DuelRow key={i} duel={d} myUid={user.uid}/>
              ))}
            </div>

            {/* Narration (if complete) */}
            {myBattle.status === "completed" && myBattle.narration && (
              <div className="tb-narration card">
                <p className="tb-section-label">CHRONIQUE DU COMBAT</p>
                <p className="tb-narration-text">{myBattle.narration.narration}</p>
                <div className="tb-narration-quotes">
                  <p className="tb-narration-cry">"{myBattle.narration.victory_cry}"</p>
                  <p className="tb-narration-defeat">"{myBattle.narration.defeat_line}"</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create */}
        {tab === "create" && (
          <div className="tb-create card" style={{maxWidth:"600px"}}>
            <p className="tb-section-label">CRÉER UNE BATTLE</p>

            <div className="field">
              <label>NOM DE LA BATTLE</label>
              <input type="text" value={battleName} onChange={e=>setBattleName(e.target.value)} placeholder="Ex: La Grande Guerre des Oni..."/>
            </div>
            <div className="field">
              <label>FORMAT</label>
              <div className="tb-size-selector">
                {[2,3].map(s => (
                  <button key={s}
                    className={`tb-size-btn${battleSize===s?" active":""}`}
                    onClick={()=>setBattleSize(s)}>
                    {s}v{s}
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <label>NOM DE TA TEAM</label>
              <input type="text" value={teamName} onChange={e=>setTeamName(e.target.value)} placeholder="Ex: Les Crab Oni Furieux..."/>
            </div>
            <div className="tb-create-preview">
              <span className="section-eyebrow">TA TEAM (TU ES CAPITAINE)</span>
              <div className="tb-member-row" style={{marginTop:"8px"}}>
                <span>{oni.emoji}</span>
                <div>
                  <p className="tb-member-name">{profile?.displayName || "Toi"} <span style={{color:"var(--gold)"}}>★ CAP</span></p>
                  <p className="tb-member-oni">{oni.name}</p>
                </div>
              </div>
              {Array(battleSize-1).fill(null).map((_,i)=>(
                <div key={i} className="tb-member-row" style={{opacity:.4}}>
                  <span>？</span>
                  <div><p className="tb-member-name">En attente...</p></div>
                </div>
              ))}
            </div>
            <button className="btn btn-primary" style={{width:"100%", justifyContent:"center"}}
              onClick={createBattle} disabled={creating||!teamName.trim()||!battleName.trim()}>
              {creating ? <><span className="spinner"/> CRÉATION...</> : "CRÉER LA BATTLE →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
